// ═══════════════════════════════════════════════════════════════════════════
// maturityConfigTab.jsx — Guided Maturity & Complexity cockpit (expert UX)
// ═══════════════════════════════════════════════════════════════════════════
// The most important screen in the app: it teaches the user where they are,
// helps them choose correctly, and reconfigures everything to match.
//
// UX pillars:
//   1. GUIDE FIRST — a self-assessment quiz infers maturity for the unsure.
//   2. TEACH EVERYWHERE — every level has plain-English meaning, "choose when",
//      "avoid when", real-world signals, and what unlocks. Tooltips throughout.
//   3. SHOW CONSEQUENCES — a live before/after preview of tabs, scope and
//      capabilities, plus honest sequencing warnings.
//   4. ONE DECISIVE ACTION — a single Apply button.
//
// Props: { state, dispatch }  ·  dispatch({ type:"SET_PROFILE", maturity, complexity })
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS } from "./theme";
import {
  MATURITY_LEVELS, MATURITY_GUIDANCE, COMPLEXITY_LEVELS, COMPLEXITY_GUIDANCE,
  ARCHITECTURE_OPTIONS, BAND_META, TAB_CATALOG,
  CAPABILITY_LABELS, MATURITY_QUIZ, inferMaturityFromQuiz,
  resolveProfile, getProfileAdvice,
} from "./maturityModel";
import { GRP_META } from "./IfacesData";

// ─── Reusable tooltip (pure CSS hover, no deps) ─────────────────────────────
function Tip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <span style={{
          position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)",
          background: "#0a1c30", color: T.text, border: `1px solid ${T.cyan}55`,
          borderRadius: 6, padding: "7px 10px", fontSize: 10, lineHeight: 1.45,
          width: 250, zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,.5)",
          fontFamily: FONTS.body, fontWeight: 400, pointerEvents: "none",
        }}>
          {text}
          <span style={{
            position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
            borderLeft: "6px solid transparent", borderRight: "6px solid transparent",
            borderTop: `6px solid ${T.cyan}55`,
          }}/>
        </span>
      )}
    </span>
  );
}

const InfoDot = ({ text }) => (
  <Tip text={text}>
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 14, height: 14, borderRadius: "50%", border: `1px solid ${T.text2}`,
      color: T.text2, fontSize: 9, marginLeft: 6, cursor: "help", flexShrink: 0,
    }}>i</span>
  </Tip>
);

export default function MaturityConfigTab({ state, dispatch }) {
  const [draftM, setDraftM]       = useState(state?.maturity ?? 3);
  const [draftC, setDraftC]       = useState(state?.complexity ?? 4);
  const [expandedLevel, setExpandedLevel] = useState(state?.maturity ?? 3);
  const [quizOpen, setQuizOpen]   = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});

  const profile = useMemo(() => resolveProfile(draftM, draftC), [draftM, draftC]);
  const advice  = useMemo(() => getProfileAdvice(draftM, draftC), [draftM, draftC]);
  const applied = state?.maturity === draftM && state?.complexity === draftC;

  const currentProfile = useMemo(
    () => resolveProfile(state?.maturity ?? 3, state?.complexity ?? 4),
    [state?.maturity, state?.complexity]
  );
  const tabsGained = profile.visibleTabs.filter(t => !currentProfile.visibleTabs.includes(t));
  const tabsLost   = currentProfile.visibleTabs.filter(t => !profile.visibleTabs.includes(t));

  const adviceColor = { warning: T.orange, info: T.cyan, success: T.green, neutral: T.text2 }[advice.tone] || T.text2;

  const quizInferred = useMemo(() => inferMaturityFromQuiz(quizAnswers), [quizAnswers]);
  const quizAnswered = Object.keys(quizAnswers).length;

  const apply = () => dispatch({ type: "SET_PROFILE", maturity: draftM, complexity: draftC });
  const labelFor = (id) => TAB_CATALOG.find(t => t.id === id)?.label || id;

  return (
    <div style={{ padding: "22px 26px", maxHeight: "calc(100vh - 108px)", overflowY: "auto", fontFamily: FONTS.body }}>

      {/* ─── Hero header ─── */}
      <div style={{
        background: `linear-gradient(135deg, ${profile.accent}18, ${T.bg2} 70%)`,
        border: `1px solid ${profile.accent}40`, borderRadius: 12,
        padding: "20px 24px", marginBottom: 20,
      }}>
        <div style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: profile.accent, marginBottom: 6 }}>
          STEP 0 · CALIBRATE THE PLATFORM TO YOUR REALITY
        </div>
        <div style={{ fontFamily: FONTS.header, fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8 }}>
          Where is your organisation today?
        </div>
        <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6, maxWidth: 760 }}>
          Set two things — your integration <strong style={{ color: T.text }}>maturity</strong> and your operational{" "}
          <strong style={{ color: T.text }}>complexity</strong>. The platform then shows only what's relevant to you:
          the right tabs, the right interfaces in scope, the right architecture recommendation. Not sure? Take the
          30-second self-assessment.
        </div>
        <button onClick={() => setQuizOpen(o => !o)} style={{
          marginTop: 14, padding: "9px 18px", borderRadius: 8,
          background: quizOpen ? T.bg3 : `${T.cyan}18`,
          border: `1px solid ${T.cyan}`, color: T.cyan,
          fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}>
          {quizOpen ? "✕ Close self-assessment" : "🧭 Not sure? Take the 30-second self-assessment"}
        </button>
      </div>

      {/* ─── Self-assessment quiz (collapsible) ─── */}
      {quizOpen && (
        <div style={{
          background: T.bg2, border: `1px solid ${T.cyan}40`, borderRadius: 10,
          padding: "18px 22px", marginBottom: 20,
        }}>
          <div style={{ fontSize: 11, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
            🧭 MATURITY SELF-ASSESSMENT
          </div>
          <div style={{ fontSize: 10.5, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
            Answer honestly, top to bottom. Maturity is a ladder — we stop counting at your first "No",
            because you can't be at a higher rung without the ones below it.
          </div>

          {MATURITY_QUIZ.map((q, i) => {
            const ans = quizAnswers[q.id];
            const locked = i > 0 && quizAnswers[MATURITY_QUIZ[i - 1].id] === false;
            return (
              <div key={q.id} style={{
                opacity: locked ? 0.4 : 1, marginBottom: 12, padding: "12px 14px",
                background: T.bg3, borderRadius: 8,
                border: `1px solid ${ans === true ? T.green + "40" : ans === false ? T.orange + "40" : T.border}`,
              }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{
                    fontFamily: FONTS.header, fontSize: 13, fontWeight: 700,
                    color: MATURITY_LEVELS[q.level].color, minWidth: 28,
                  }}>L{q.level}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.5, marginBottom: 8 }}>{q.text}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button disabled={locked} onClick={() => setQuizAnswers(a => ({ ...a, [q.id]: true }))}
                        style={quizBtn(ans === true, T.green)}>Yes</button>
                      <button disabled={locked} onClick={() => setQuizAnswers(a => ({ ...a, [q.id]: false }))}
                        style={quizBtn(ans === false, T.orange)}>No</button>
                      {ans !== undefined && (
                        <span style={{ fontSize: 9.5, color: ans ? T.green : T.orange, fontStyle: "italic" }}>
                          {ans ? q.yesMeans : q.noMeans}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {quizAnswered > 0 && (
            <div style={{
              marginTop: 14, padding: "14px 16px", borderRadius: 8,
              background: `${MATURITY_LEVELS[quizInferred].color}15`,
              border: `1px solid ${MATURITY_LEVELS[quizInferred].color}`,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
            }}>
              <div>
                <div style={{ fontSize: 10, color: T.text2, marginBottom: 2 }}>Your assessed maturity:</div>
                <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: MATURITY_LEVELS[quizInferred].color }}>
                  {MATURITY_LEVELS[quizInferred].key} · {MATURITY_LEVELS[quizInferred].label}
                </div>
              </div>
              <button onClick={() => { setDraftM(quizInferred); setExpandedLevel(quizInferred); }}
                style={{
                  padding: "10px 18px", borderRadius: 8,
                  background: MATURITY_LEVELS[quizInferred].color, border: "none",
                  color: "#001018", fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                ✓ Use {MATURITY_LEVELS[quizInferred].key} as my maturity →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Two-column ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* LEFT — maturity ladder */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.text, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase" }}>
              1 · Choose your maturity level
            </span>
            <InfoDot text="Maturity = how evolved your integration capability is. It's a ladder (L0→L5): you can't be at a higher level without the foundations of the ones below. This is about HOW you integrate, not how big you are." />
          </div>

          {Object.values(MATURITY_LEVELS).map(lvl => {
            const sel = draftM === lvl.id;
            const open = expandedLevel === lvl.id;
            const g = MATURITY_GUIDANCE[lvl.id];
            return (
              <div key={lvl.id} style={{
                marginBottom: 8, borderRadius: 10, overflow: "hidden",
                border: `1px solid ${sel ? lvl.color : T.border}`,
                background: sel ? `${lvl.color}10` : T.bg2,
                boxShadow: sel ? `0 0 0 1px ${lvl.color}55` : "none",
                transition: "all .15s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", cursor: "pointer" }}
                  onClick={() => { setDraftM(lvl.id); setExpandedLevel(open ? -1 : lvl.id); }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 38 }}>
                    <div style={{ fontFamily: FONTS.header, fontSize: 17, fontWeight: 700, color: lvl.color }}>{lvl.key}</div>
                    <div style={{ display: "flex", gap: 2, marginTop: 3 }}>
                      {[0,1,2,3,4,5].map(r => (
                        <div key={r} style={{ width: 4, height: 4, borderRadius: "50%", background: r <= lvl.id ? lvl.color : T.bg4 }}/>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: sel ? T.text : T.text2 }}>{lvl.label}</span>
                      {lvl.id === 3 && <span style={recoBadge}>RECOMMENDED DEFAULT</span>}
                    </div>
                    <div style={{ fontSize: 9.5, color: T.text2, marginTop: 2, fontStyle: "italic" }}>{g.oneLiner}</div>
                  </div>
                  {sel && <span style={{ color: lvl.color, fontSize: 17 }}>✓</span>}
                  <span style={{ color: T.text3, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
                </div>

                {open && (
                  <div style={{ padding: "4px 16px 16px", borderTop: `1px solid ${lvl.color}25` }}>
                    <p style={{ fontSize: 11, color: T.text, lineHeight: 1.6, margin: "12px 0" }}>{g.plainEnglish}</p>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
                      <GuidanceList color={T.green} icon="✓" title="CHOOSE THIS WHEN" items={g.chooseWhen}/>
                      <GuidanceList color={T.orange} icon="✕" title="AVOID / LOOK HIGHER IF" items={g.avoidWhen}/>
                    </div>

                    <GuidanceList color={T.cyan} icon="•" title="REAL-WORLD SIGNALS YOU'RE HERE" items={g.realWorldSignals} full/>

                    <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <MiniFact label="Typical organisation" value={g.typicalOrg}/>
                      <MiniFact label="What unlocks in the app" value={g.whatYouGet}/>
                    </div>

                    <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: T.bg3, border: `1px dashed ${lvl.color}40` }}>
                      <div style={{ fontSize: 8, color: lvl.color, letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                        ↗ HOW TO REACH THE NEXT LEVEL
                      </div>
                      <div style={{ fontSize: 10.5, color: T.text, lineHeight: 1.5 }}>{g.nextStep}</div>
                      <div style={{ fontSize: 9, color: T.text2, marginTop: 4, fontFamily: FONTS.mono }}>
                        Typical effort: {g.effortToReachNext}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT — complexity + recommendation */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.text, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase" }}>
              2 · Choose your complexity
            </span>
            <InfoDot text="Complexity = the scale and constraints of your operation: how many plants, systems, interfaces, and what regulatory burden. Unlike maturity, this is about HOW BIG and HOW CONSTRAINED you are, not how evolved." />
          </div>

          <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
            {Object.values(COMPLEXITY_LEVELS).map(lvl => {
              const sel = draftC === lvl.id;
              return (
                <Tip key={lvl.id} text={COMPLEXITY_GUIDANCE[lvl.id]?.plainEnglish || ""}>
                  <button onClick={() => setDraftC(lvl.id)}
                    style={{
                      width: 64, background: sel ? `${lvl.color}20` : T.bg2,
                      border: `1px solid ${sel ? lvl.color : T.border}`,
                      borderRadius: 8, padding: "10px 4px", cursor: "pointer", textAlign: "center",
                    }}>
                    <div style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: lvl.color }}>{lvl.key}</div>
                    <div style={{ fontSize: 8, color: sel ? T.text : T.text2, marginTop: 2 }}>{lvl.label}</div>
                  </button>
                </Tip>
              );
            })}
          </div>
          <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 18 }}>
            <div style={{ fontSize: 8, color: COMPLEXITY_LEVELS[draftC].color, letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
              WHAT "{COMPLEXITY_LEVELS[draftC].label.toUpperCase()}" MEANS
            </div>
            <div style={{ fontSize: 10, color: T.text2, marginBottom: 8, lineHeight: 1.5 }}>
              {COMPLEXITY_GUIDANCE[draftC]?.plainEnglish}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {(COMPLEXITY_GUIDANCE[draftC]?.drivers || []).map((d, i) => (
                <span key={i} style={{
                  fontSize: 9, padding: "3px 9px", borderRadius: 10,
                  background: `${COMPLEXITY_LEVELS[draftC].color}12`,
                  color: COMPLEXITY_LEVELS[draftC].color,
                  border: `1px solid ${COMPLEXITY_LEVELS[draftC].color}35`,
                }}>{d}</span>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, color: T.text, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>
            Your recommended architecture
          </div>
          <div style={{
            background: `linear-gradient(135deg, ${profile.accent}1a, ${T.bg2})`,
            border: `1px solid ${profile.accent}55`, borderRadius: 12, padding: "16px 18px", marginBottom: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 30 }}>{profile.bandMeta?.icon}</span>
              <div>
                <div style={{ fontSize: 8, color: profile.bandMeta?.color, fontFamily: FONTS.mono, letterSpacing: 2, fontWeight: 700 }}>
                  {profile.band}
                </div>
                <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
                  #{profile.architecture.n} · {profile.architecture.name}
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px" }}>
              <KV label="Topology" value={profile.architecture.topology} tip="The physical/logical shape of how systems connect at this architecture." />
              <KV label="Time to value" value={profile.architecture.ttv} tip="Typical elapsed time from project start to first production value." />
              <KV label="Typical cost" value={profile.architecture.cost} tip="Indicative all-in programme cost range — software, integration, services." />
              <KV label="Your profile" value={`${profile.maturityMeta.key} · ${profile.complexityMeta.key}`} tip="Your selected maturity & complexity coordinates on the 15-option matrix." />
            </div>
          </div>

          <div style={{
            padding: "12px 14px", borderRadius: 10, marginBottom: 12,
            background: `${adviceColor}12`, border: `1px solid ${adviceColor}45`, borderLeft: `4px solid ${adviceColor}`,
          }}>
            <div style={{ fontSize: 9, color: adviceColor, fontWeight: 700, letterSpacing: 1, marginBottom: 5 }}>
              {advice.tone === "warning" ? "⚠️ SEQUENCING WARNING" :
               advice.tone === "success" ? "✓ STRONG FIT" :
               advice.tone === "info"    ? "ℹ️ WORTH CONSIDERING" : "GUIDANCE"}
            </div>
            <div style={{ fontSize: 10.5, color: T.text, lineHeight: 1.55 }}>{advice.text}</div>
          </div>

          <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 9, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700 }}>
                CAPABILITIES AT THIS LEVEL
              </span>
              <InfoDot text="These are the integration capabilities that become meaningful at your chosen maturity. Greyed-out ones unlock at higher levels." />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {Object.entries(CAPABILITY_LABELS).map(([key, label]) => {
                const on = profile.capabilities[key];
                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, color: on ? T.green : T.text3 }}>
                    <span>{on ? "✓" : "○"}</span>
                    <span style={{ textDecoration: on ? "none" : "line-through" }}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Before / after impact preview ─── */}
      <div style={{ background: T.bg2, border: `1px solid ${profile.accent}30`, borderRadius: 10, padding: "16px 18px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 10, color: profile.accent, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700 }}>
            WHAT CHANGES WHEN YOU APPLY
          </span>
          <InfoDot text="A preview of how the app reconfigures. Tabs and interface groups appear or hide; nothing in your tracked progress is ever deleted — hidden items return if you raise maturity again." />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ fontSize: 9, color: T.green, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              ▲ TABS THAT APPEAR ({tabsGained.length})
            </div>
            {tabsGained.length === 0
              ? <div style={{ fontSize: 9.5, color: T.text3, fontStyle: "italic" }}>None — same or fewer than now.</div>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{tabsGained.map(id => <span key={id} style={chip(T.green)}>{labelFor(id)}</span>)}</div>}
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.orange, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              ▼ TABS THAT HIDE ({tabsLost.length})
            </div>
            {tabsLost.length === 0
              ? <div style={{ fontSize: 9.5, color: T.text3, fontStyle: "italic" }}>None — nothing is removed.</div>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{tabsLost.map(id => <span key={id} style={chip(T.orange)}>{labelFor(id)}</span>)}</div>}
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.cyan, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              ◆ INTERFACE GROUPS IN SCOPE ({profile.groupsInScope.length})
            </div>
            {profile.groupsInScope.length === 0
              ? <div style={{ fontSize: 9.5, color: T.text3, fontStyle: "italic" }}>None — manual programme; readiness tracking only.</div>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {Object.keys(GRP_META).map(grp => {
                    const inScope = profile.groupsInScope.includes(grp);
                    const c = GRP_META[grp]?.color || T.steel;
                    return <span key={grp} style={{ ...chip(inScope ? c : T.text3), opacity: inScope ? 1 : 0.4, borderStyle: inScope ? "solid" : "dashed" }}>{inScope ? "" : "🔒 "}{grp}</span>;
                  })}
                </div>}
          </div>
        </div>
      </div>

      {/* ─── Apply bar ─── */}
      <div style={{ position: "sticky", bottom: 0, marginTop: 8, background: `linear-gradient(0deg, ${T.bg} 60%, transparent)`, padding: "14px 0 6px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: T.text }}>
            Selected: <strong style={{ color: profile.accent }}>{profile.maturityMeta.key} {profile.maturityMeta.label}</strong>
            {" · "}<strong style={{ color: COMPLEXITY_LEVELS[draftC].color }}>{profile.complexityMeta.key} {profile.complexityMeta.label}</strong>
            {" → "}<strong style={{ color: T.text }}>#{profile.architecture.n} {profile.architecture.name}</strong>
          </div>
          {!applied && <div style={{ fontSize: 9, color: T.text3, marginTop: 2 }}>Applying reconfigures tabs, interface scope and capabilities across the whole platform. Your tracked progress is preserved.</div>}
        </div>
        <button onClick={apply} disabled={applied}
          style={{
            padding: "13px 28px", borderRadius: 10,
            background: applied ? T.bg3 : `linear-gradient(135deg, ${profile.accent}, ${T.cyan})`,
            border: `1px solid ${applied ? T.border : profile.accent}`,
            color: applied ? T.text2 : "#001018",
            fontFamily: FONTS.header, fontSize: 13, fontWeight: 700,
            cursor: applied ? "default" : "pointer", letterSpacing: 1, whiteSpace: "nowrap",
          }}>
          {applied ? "✓ PROFILE ACTIVE" : "▶ APPLY THIS PROFILE"}
        </button>
      </div>

      {/* ─── Full matrix reference ─── */}
      <details style={{ marginTop: 20 }}>
        <summary style={{ cursor: "pointer", fontSize: 10, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, padding: "8px 0" }}>
          ▸ SHOW ALL 15 ARCHITECTURE OPTIONS (reference matrix)
        </summary>
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9.5 }}>
            <thead>
              <tr style={{ background: T.bg3 }}>
                {["#","Architecture","Band","Mat.","Cplx","Topology","TTV","Cost"].map(h => (
                  <th key={h} style={{ padding: "7px 8px", textAlign: "left", color: T.text2, fontFamily: FONTS.mono, fontSize: 8, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ARCHITECTURE_OPTIONS.map(o => {
                const isPick = o.n === profile.architecture.n;
                const bandC = BAND_META[o.band]?.color || T.steel;
                return (
                  <tr key={o.n} onClick={() => { setDraftM(o.maturity); setDraftC(o.complexity); setExpandedLevel(o.maturity); }}
                    style={{ borderBottom: `1px solid ${T.border2}`, background: isPick ? `${profile.accent}18` : "transparent", cursor: "pointer" }}>
                    <td style={{ padding: "6px 8px", fontFamily: FONTS.mono, color: isPick ? profile.accent : T.text3, fontWeight: 700 }}>{isPick ? "▶ " : ""}{o.n}</td>
                    <td style={{ padding: "6px 8px", color: isPick ? T.text : T.text2, fontWeight: isPick ? 700 : 400 }}>{o.name}</td>
                    <td style={{ padding: "6px 8px" }}><span style={{ color: bandC, fontSize: 8, fontFamily: FONTS.mono }}>{o.band}</span></td>
                    <td style={{ padding: "6px 8px", color: MATURITY_LEVELS[o.maturity]?.color, fontFamily: FONTS.mono }}>{MATURITY_LEVELS[o.maturity]?.key}</td>
                    <td style={{ padding: "6px 8px", color: COMPLEXITY_LEVELS[o.complexity]?.color, fontFamily: FONTS.mono }}>{COMPLEXITY_LEVELS[o.complexity]?.key}</td>
                    <td style={{ padding: "6px 8px", color: T.text2, fontSize: 9 }}>{o.topology}</td>
                    <td style={{ padding: "6px 8px", color: T.text2, fontFamily: FONTS.mono, fontSize: 8.5 }}>{o.ttv}</td>
                    <td style={{ padding: "6px 8px", color: T.amber, fontFamily: FONTS.mono, fontSize: 8.5 }}>{o.cost}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ─── Small presentational helpers ──────────────────────────────────────────
function GuidanceList({ color, icon, title, items, full }) {
  return (
    <div style={full ? { gridColumn: "1 / -1" } : undefined}>
      <div style={{ fontSize: 8, color, letterSpacing: 1, fontWeight: 700, marginBottom: 5 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 6, fontSize: 9.5, color: T.text2, lineHeight: 1.4 }}>
            <span style={{ color, flexShrink: 0 }}>{icon}</span>
            <span>{it}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniFact({ label, value }) {
  return (
    <div style={{ background: T.bg3, borderRadius: 6, padding: "8px 10px" }}>
      <div style={{ fontSize: 7.5, color: T.text3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 9.5, color: T.text, lineHeight: 1.45 }}>{value}</div>
    </div>
  );
}

function KV({ label, value, tip }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", fontSize: 7.5, color: T.text3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
        {label}{tip && <InfoDot text={tip} />}
      </div>
      <div style={{ fontSize: 10.5, color: T.text, lineHeight: 1.35 }}>{value}</div>
    </div>
  );
}

const quizBtn = (active, color) => ({
  padding: "5px 16px", borderRadius: 6, cursor: "pointer",
  background: active ? color : "transparent",
  border: `1px solid ${active ? color : T.border}`,
  color: active ? "#001018" : T.text2,
  fontFamily: FONTS.header, fontSize: 11, fontWeight: 700,
});

const chip = (color) => ({
  fontSize: 9, padding: "3px 8px", borderRadius: 10, fontFamily: FONTS.mono,
  background: `${color}12`, color, border: `1px solid ${color}35`,
});

const recoBadge = {
  fontSize: 7.5, padding: "2px 7px", borderRadius: 8, fontFamily: FONTS.mono, fontWeight: 700,
  background: "#38a8ff20", color: "#38a8ff", border: "1px solid #38a8ff60", letterSpacing: 1,
};
